import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { contentStyles, h1, h4, tagLarge } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Button/Button.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/SectionWrapper/SectionWrapper.styles';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  padding: 1px 0;

  &[data-background='beige'] {
    --bgColor: ${brand.beige};
    padding: ${fluid(24, 42)} 0;
  }

  &[data-background='white'] {
    --bgColor: ${brand.white};
    margin: ${fluid(24, 42)} 0;
  }

  background-color: var(--bgColor);

  [data-spacing-below='false'] & {
    margin-bottom: 0;
  }

  ${SectionWrapperStyles.Inner} {
    @media ${from(Device.TabletLarge)} {
      --siteWidth: 1920px;

      width: 100%;
      max-width: var(--maxWidth);
    }
  }
`;
const Inner = styled.div `
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  place-content: center;

  @media ${from(Device.TabletLarge)} {
    gap: 16px;
  }

  @media ${from(Device.Desktop)} {
    [data-has-video-callout='true'] &,
    [data-has-review-badge='true'] & {
      grid-template-columns: repeat(32, 1fr);
    }
  }
`;
const Content = styled.div `
  grid-column: span 24;
  text-align: center;

  /* When mobile badge displays, it should sit above Content */
  [data-mobile-badge='true'] & {
    order: 2;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 5 / span 16;
    padding: 0;

    [data-mobile-badge='true'] & {
      order: 2;
    }
  }

  @media ${from(Device.Desktop)} {
    grid-column: 6 / span 14;

    [data-has-review-badge='true'] &,
    [data-has-video-callout='true'] & {
      grid-column: 8 / span 18;
    }
  }
`;
const Title = styled.h1 `
  ${h1};

  @media ${from(Device.Tablet)} {
    &[data-invisible='true'] {
      color: var(--bgColor);
    }
  }
`;
const SubTitle = styled.span `
  ${tagLarge};

  margin-bottom: 4px;
`;
const Buttons = styled.div `
  display: flex;
  gap: 16px;
  flex-direction: column;
  margin-top: 40px;

  ${ButtonStyles.Container} {
    justify-content: center;
  }

  @media ${from(Device.Tablet)} {
    flex-direction: row;
    justify-content: center;
    gap: 40px;
  }
`;
const BadgeContainer = styled.div `
  @media ${until(Device.TabletLarge)} {
    [data-mobile-badge='false'] & {
      display: none;
    }

    [data-mobile-badge='true'] & {
      display: block;
      grid-column: 10 / span 6;
      margin-bottom: 12px;
      /* When mobile badge displays, it should sit above Content */
      order: 1;
    }

    [data-mobile-badge='true'][data-double-badge='true'] & {
      grid-column: 14 / span 6;

      &[data-before] {
        grid-column: 6 / span 6;
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    display: flex;
    grid-column: 21 / span 4;
    place-content: center;
    margin-bottom: 0;

    > div {
      padding-left: 8%;
      margin: auto;
    }

    [data-mobile-badge='true'] & {
      order: 2;
    }

    &[data-before] {
      grid-column: 1 / span 4;

      > div {
        padding-left: 0;
        padding-right: 8%;
      }
    }
  }

  @media ${from(Device.Desktop)} {
    > div {
      padding-left: 16%;
    }

    &[data-before] > div {
      padding-right: 16%;
    }
  }
`;
const Badge = styled.img `
  width: 100%;
`;
const VideoContainer = styled.div `
  display: flex;
  grid-column: span 24;
  padding: 40px 18px 0;
  text-align: center;

  @media ${from(Device.Desktop)} {
    grid-column: 27 / span 6;
    padding: 0;
    text-align: left;
  }
`;
const VideoInner = styled.div `
  background-color: ${brand.green};
  margin: auto;
  padding: 26px;

  @media ${from(Device.Tablet)} {
    padding: 40px;
  }
`;
const VideoTitle = styled.span `
  ${h4};
  color: ${brand.white};
`;
const VideoButton = styled.button `
  ${fonts.brandonGrotesque.bold};
  background: none;
  border: 0;
  font-size: 18px;
  line-height: 24px;
  text-transform: uppercase;
  color: ${brand.white};
  padding: 3px 0;
  gap: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;

  @media ${from(Device.Tablet)} {
    justify-content: flex-start;
  }

  svg {
    color: ${brand.white};
    width: 22px;
    height: 22px;
  }

  span::after {
    content: '';
    background-color: ${brand.white};
    height: 2px;
    max-width: 375px;
    width: 100%;
    display: block;
    ${transition('max-width')};
  }

  &:hover {
    span::after {
      max-width: 0;
    }
  }
`;
const TextContent = styled.div `
  ${contentStyles};

  p:last-child {
    margin-bottom: 0;
  }

  margin: 0 auto;
`;
const ReviewsInner = styled.div `
  margin: auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;

  > svg {
    display: block;
    width: ${fluid(150, 175)};
  }
`;
const ReviewLabel = styled.p `
  ${fonts.brandonGrotesque.regular};

  font-size: ${fluid(18, 26)};
  line-height: ${fluid(24, 36)};
  letter-spacing: -0.03em;
`;
const Stars = styled.div `
  --starSize: 19px;

  height: var(--starSize);
  margin: 0 ${fluid(16, 22)} 0 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin: 0 auto;
  height: 36px;
`;
const Rating = styled.p `
  ${fonts.brandonGrotesque.medium};

  margin-right: 6px;
  font-size: 26px;
  line-height: 36px;
  letter-spacing: -0.03em;
`;
const Star = styled.span `
  display: grid;
`;
const StarInner = styled.span `
  width: var(--starSize);
  max-width: calc(var(--starSize) * var(--fill, 1));
  overflow: hidden;
  color: #fdd663;
  grid-area: 1 / 1 / 1/ 1;

  &[data-bg] {
    color: #e8e8e8;
  }

  > svg {
    display: block;
    width: var(--starSize);
  }
`;
const RatingLink = styled.a `
  ${fonts.brandonGrotesque.light};
  ${transition('text-decoration-color')};

  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: currentColor;
  color: #7b7b7b;
  font-size: 16px;
  line-height: 36px;
  letter-spacing: -0.03em;

  &:hover {
    text-decoration-color: transparent;
  }
`;
const InnerPageHeroBannerStyles = {
    Container,
    Inner,
    Content,
    Title,
    SubTitle,
    Buttons,
    BadgeContainer,
    Badge,
    VideoContainer,
    VideoInner,
    VideoTitle,
    VideoButton,
    TextContent,
    ReviewsInner,
    ReviewLabel,
    Stars,
    Star,
    StarInner,
    Rating,
    RatingLink,
};
export default InnerPageHeroBannerStyles;
